import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, ClickAwayListener, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { useContext, useRef, useState, type ReactNode } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';
import { areaFromPath } from '@/layouts/navigation';

export interface InfoTipProps {
  /** What the thing next to it does, in a sentence or two. */
  title: ReactNode;
  /** Names the bubble for screen readers, e.g. "About Sale mode". */
  label?: string;
}

/**
 * A small (i) next to a label that explains it. With a mouse it shows while the
 * pointer is over it (or it has keyboard focus); on a touch screen a tap opens
 * it and it stays until the next tap on it or anywhere else. Escape closes it.
 * For the provider and admin portals only: learner screens stay uncluttered,
 * so it renders nothing there (the bank editor is shared by both).
 */
/** Info bubbles show in the provider and admin portals, never on learner screens. */
export function useShowTips() {
  // Read the context directly so a component shown outside a router (in a test) just has no bubbles.
  const pathname = useContext(UNSAFE_LocationContext)?.location?.pathname;
  return !!pathname && areaFromPath(pathname) !== 'learner';
}

/** Focus from the keyboard (not a mouse click). Assumes the keyboard where the browser can't say. */
function keyboardFocus(el: HTMLElement) {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

export function InfoTip({ title, label = 'More information' }: InfoTipProps) {
  const show = useShowTips();
  const [open, setOpen] = useState(false);
  // Whether the bubble was open just before the last press (the browser's mouse emulation after a tap opens it first).
  const wasOpen = useRef(false);
  const pointerType = useRef('mouse');
  if (!show) return null;
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
        <Tooltip
          title={title}
          open={open}
          onClose={() => setOpen(false)} // Escape
          arrow
          // Opening and closing are done below, so a tap isn't also treated as a hover or a long press.
          disableHoverListener
          disableFocusListener
          disableTouchListener
          slotProps={{
            // Solid, so the text behind it doesn't show through.
            tooltip: { sx: { fontSize: '0.8125rem', lineHeight: 1.45, maxWidth: 320, p: 1.25, bgcolor: 'grey.900', color: 'common.white' } },
            arrow: { sx: { color: 'grey.900' } },
          }}
        >
          <IconButton
            size="small"
            aria-label={label}
            // A mouse: shows while the pointer is over it. Touch is handled by the tap (click) below.
            onPointerEnter={(e) => e.pointerType === 'mouse' && setOpen(true)}
            onPointerLeave={(e) => e.pointerType === 'mouse' && setOpen(false)}
            // The keyboard: shows while it has focus (not after a mouse click).
            onFocus={(e) => keyboardFocus(e.currentTarget) && setOpen(true)}
            onBlur={() => setOpen(false)}
            onPointerDown={(e) => {
              pointerType.current = e.pointerType;
              wasOpen.current = open;
            }}
            onClick={(e) => {
              // Inside a label, a switch or a card header the tap is for the bubble only.
              e.preventDefault();
              e.stopPropagation();
              if (e.detail === 0) setOpen((o) => !o); // Enter or Space
              else if (pointerType.current === 'mouse') setOpen(true); // a click keeps the hover bubble up
              else setOpen(!wasOpen.current); // a tap toggles; it stays up until the next tap on it or elsewhere
            }}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ p: 0.25, ml: 0.25, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
}

/** A label followed by its info bubble, for form fields, switches and headings. */
export function LabelWithTip({ children, tip }: { children: ReactNode; tip: ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {children}
      <InfoTip title={tip} label={typeof children === 'string' ? `About ${children}` : undefined} />
    </Box>
  );
}

/**
 * An info bubble at the end of a text field or select:
 * `slotProps={{ input: { endAdornment: <TipAdornment tip="…" /> } }}`.
 * Pass `select` so it sits clear of the dropdown arrow.
 */
export function TipAdornment({ tip, label, select = false }: { tip: ReactNode; label?: string; select?: boolean }) {
  const show = useShowTips();
  if (!show) return null;
  return (
    <InputAdornment position="end" sx={select ? { mr: 2.5 } : undefined}>
      <InfoTip title={tip} label={label} />
    </InputAdornment>
  );
}
